/**
 * Image Uploader Component
 * Drag-and-drop file upload with preview and progress
 */
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  IconButton,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Close,
  Image as ImageIcon,
} from '@mui/icons-material';
import ipfsUploadService, { UploadProgress } from '../services/ipfsUpload.service';

interface ImageUploaderProps {
  onUploadComplete: (cid: string, ipfsUri: string, gatewayUrl: string) => void;
  accept?: Record<string, string[]>;
  maxSize?: number;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  onUploadComplete,
  accept = {
    'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'],
  },
  maxSize = 100 * 1024 * 1024, // 100MB
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<{
    cid: string;
    ipfsUri: string;
    gatewayUrl: string;
    filename: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setError(null);

      // Validate file
      const validation = ipfsUploadService.validateFile(file, maxSize / (1024 * 1024));
      if (!validation.valid) {
        setError(validation.error || 'File validation failed');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload file
      setUploading(true);
      setProgress({ loaded: 0, total: file.size, percentage: 0 });

      try {
        const result = await ipfsUploadService.uploadFile(file, (prog) => {
          setProgress(prog);
        });

        if (result.success && result.cid) {
          setUploadedFile({
            cid: result.cid,
            ipfsUri: result.ipfsUri,
            gatewayUrl: result.ipfsGateways.filebase,
            filename: result.filename,
          });
          onUploadComplete(result.cid, result.ipfsUri, result.ipfsGateways.filebase);
        } else {
          setError('Upload succeeded but CID not available yet. Try again in a moment.');
        }
      } catch (err: any) {
        setError(err.message || 'Upload failed');
        setPreview(null);
      } finally {
        setUploading(false);
        setProgress(null);
      }
    },
    [maxSize, onUploadComplete]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple: false,
  });

  const handleReset = () => {
    setPreview(null);
    setUploadedFile(null);
    setError(null);
    setProgress(null);
  };

  return (
    <Box>
      {!uploadedFile ? (
        <Paper
          {...getRootProps()}
          sx={{
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            bgcolor: isDragActive ? 'primary.50' : 'background.paper',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'primary.50',
              transform: 'translateY(-2px)',
            },
          }}
        >
          <input {...getInputProps()} />

          {preview ? (
            <Box>
              <Box
                component="img"
                src={preview}
                alt="Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 300,
                  borderRadius: 2,
                  mb: 2,
                }}
              />
              {uploading && progress && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Uploading... {progress.percentage}%
                  </Typography>
                  <LinearProgress variant="determinate" value={progress.percentage} />
                </Box>
              )}
            </Box>
          ) : (
            <Stack spacing={2} alignItems="center">
              <CloudUpload sx={{ fontSize: 64, color: 'primary.main', opacity: 0.7 }} />
              <Typography variant="h6" color="text.primary">
                {isDragActive ? 'Drop your image here' : 'Drag & drop image here'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                or click to browse
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Supported: PNG, JPG, GIF, SVG, WebP (max {maxSize / (1024 * 1024)}MB)
              </Typography>
            </Stack>
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: 3, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.main' }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <CheckCircle sx={{ color: 'success.main', mt: 0.5 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" color="success.dark" gutterBottom>
                Upload Successful!
              </Typography>
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Filename:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {uploadedFile.filename}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    IPFS URI:
                  </Typography>
                  <Chip
                    label={uploadedFile.ipfsUri}
                    size="small"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    CID:
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                    {uploadedFile.cid}
                  </Typography>
                </Box>
              </Stack>
              {preview && (
                <Box
                  component="img"
                  src={preview}
                  alt="Uploaded"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    borderRadius: 2,
                    mt: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                  }}
                />
              )}
            </Box>
            <IconButton onClick={handleReset} size="small">
              <Close />
            </IconButton>
          </Stack>
        </Paper>
      )}

      {error && (
        <Alert
          severity="error"
          icon={<ErrorIcon />}
          onClose={() => setError(null)}
          sx={{ mt: 2 }}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default ImageUploader;
